import React from 'react'

export interface ArticleSection {
  heading: string
  content: string
  bullets?: string[]
}

export interface Article {
  slug: string
  title: string
  category: string
  preview: string
  readTime: string
  iconName: string
  bgGradient: string
  categoryBg: string
  categoryText: string
  intro: string
  keyTakeaways: string[]
  sections: ArticleSection[]
  meaningForYou: string
  suggestionTitle: string
  suggestionContent: string
  relatedSlugs: string[]
  // Optional editorial fields
  subtitle?: string
  heroImage?: string
  images?: { afterSection: number; src: string; alt: string }[]
  // Optional conversion-focused fields
  ctaButton?: { text: string; link: string }
  productIntegration?: { heading: string; content: string }
  trustPoints?: string[]
  midArticleCtaAfterSection?: number
}

export const articles: Article[] = [
  {
    slug: 'frauen-schlafmangel',
    title: 'Warum Frauen plötzlich in der U-Bahn einschlafen',
    subtitle:
      'Was hinter den viralen Videos steckt – und warum viele das eigentliche Problem unterschätzen',
    category: 'Schlaf',
    preview:
      'Warum schlafen plötzlich Frauen im Zug einfach ein? Studien zeigen: Hinter der Erschöpfung steckt mehr als nur zu wenig Schlaf.',
    readTime: '8 Min. Lesezeit',
    iconName: 'MoonIcon',
    bgGradient: 'from-[#F0EBF5] to-white',
    categoryBg: 'bg-[#F0EBF5]',
    categoryText: 'text-[#8B7BAF]',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/mqJRKwSaU1L3fyguW7uvtt/7.jpg',
    intro:
      'Im März 2026 verbreiteten sich in sozialen Netzwerken Videos aus deutschen Städten: In U-Bahn und S-Bahn steigen Frauen ein, setzen sich hin, ziehen Schlafmasken auf – und schlafen einfach ein.',
    keyTakeaways: [
      'Frauen brauchen biologisch bedingt mehr Schlaf als Männer – rund 20 Minuten',
      'Chronische Erschöpfung ist oft kein reines Schlafproblem, sondern ein Zeichen für tieferliegende Ursachen',
      'Die Position von Kopf und Nacken im Schlaf beeinflusst die Erholungsqualität erheblich',
      'Kleine Veränderungen können einen großen Unterschied machen',
    ],
    sections: [
      {
        heading: 'Das ist nicht nur Müdigkeit',
        content:
          'Viele Nutzer fragten sich: Ist das ein Protest, ein Experiment oder ein neuer Trend? Doch schnell wurde klar, dass hinter den Videos mehr steckt.',
      },
      {
        heading: '20 Minuten, die sich summieren',
        content:
          'Was zunächst wie ein inszenierter Moment wirkte, entpuppte sich als stiller Ausdruck von Überforderung. Hinter den Szenen steckt kein Marketing, sondern ein Gefühl, das viele Frauen teilen: ständig funktionieren zu müssen, mehrere Rollen gleichzeitig zu tragen und dabei immer wieder die eigenen Bedürfnisse zurückzustellen. Chronischer Schlafmangel ist dabei keine Nebensache, sondern eine direkte Folge dieses dauerhaften Drucks – und er trifft Frauen oft stärker als Männer.',
        bullets: [],
      },
      {
        heading: 'Erschöpft trotz genug Schlaf',
        content:
          'Als Mindestmaß gelten etwa sechs Stunden Schlaf pro Nacht. Doch der britische Schlafforscher Prof. Jim Horne von der Loughborough University kam zu dem Ergebnis, dass Frauen im Durchschnitt rund 20 Minuten mehr Schlaf benötigen als Männer – unter anderem aufgrund der komplexeren Gehirnaktivität.\n\nGenau diese 20 Minuten fehlen jedoch vielen Frauen regelmäßig. Während der Rest der Familie bereits schläft, erledigen sie noch Aufgaben, organisieren den nächsten Tag oder kümmern sich um andere.\n\nWas zunächst wenig klingt, summiert sich:\nüber 2 Stunden pro Woche, etwa 10 Stunden im Monat und mehr als 120 Stunden im Jahr – das entspricht fast fünf Tagen Schlaf, die fehlen.\n\nBetroffen sind dabei ganz normale Frauen zwischen 25 und 55 Jahren, die tagsüber arbeiten und abends oft die Hauptverantwortung für Haushalt, Familie und Organisation übernehmen.',
        bullets: [
          'Müdigkeit am Morgen',
          'Verspannungen im Nacken',
          'Häufiges Aufwachen nachts',
        ],
      },
      {
        heading: 'Nicht nur die Dauer zählt – sondern die Qualität',
        content:
          'Diese Belastungen sind in unterschiedlichem Maß fast jeder Frau vertraut – und an sich selbst zu denken, gelingt dabei oft kaum.',
        bullets: [
          'Schmalere Schultern',
          'Beweglichere Nackenstrukturen',
          'Weniger Muskelstabilität im Nackenbereich',
        ],
      },
      {
        heading: 'Was das für die Erholung bedeutet',
        content:
          'Immerhin haben sich britische Forscher diesem Thema genauer gewidmet und 2024 das Projekt „Hannah" vorgestellt – eine 3D-Darstellung, die zeigt, wie sich jahrelanger Schlafmangel sichtbar auf den Körper einer Frau auswirken kann.\n\nWas dabei besonders auffällt, sind die äußeren Veränderungen: fahle, müde Haut, erste Falten deutlich früher, ein erschöpfter Gesichtsausdruck – und bei manchen sogar dünner werdendes Haar.\n\nMit der Zeit verliert die Haut an Spannkraft, wirkt weniger frisch, und die natürliche Ausstrahlung lässt nach.\n\nUnd genau hier liegt das Problem:\nDiese Veränderungen passieren nicht plötzlich, sondern schleichend – Tag für Tag, oft unbemerkt.\n\nViele Frauen fühlen sich irgendwann einfach „älter", müder und weniger belastbar, ohne genau zu wissen, warum.\n\nDer Auslöser ist dabei oft derselbe: dauerhafter Schlafmangel, der dem Körper über Jahre hinweg die Möglichkeit nimmt, sich wirklich zu regenerieren.',
        bullets: [
          'Dass Muskeln angespannt bleiben',
          'Dass der Körper nicht vollständig entspannt',
          'Dass der Schlaf oberflächlicher wird',
        ],
      },
      {
        heading: 'Das Problem mit herkömmlichen Kissen',
        content:
          'Ein entscheidender Faktor liegt im Nacken. In diesem Bereich verlaufen wichtige Nervenverbindungen, die eng mit Entspannung und Schlafqualität zusammenhängen. Ist der Nacken nachts nicht richtig gelagert, bleibt der Körper in einem Spannungszustand – selbst wenn man mehrere Stunden schläft.',
      },
      {
        heading: 'Was immer mehr Menschen verändern',
        content:
          'Gerade Frauen sind davon häufiger betroffen: schmalere Schultern, eine beweglichere Nackenstruktur und weniger Stabilität führen dazu, dass schon kleine Fehlstellungen die Entspannung verhindern.',
      },
    ],
    meaningForYou:
      'Frauen sind häufiger von Erschöpfung betroffen – doch oft liegt es nicht nur an der Schlafdauer. Manchmal macht eine kleine Veränderung den entscheidenden Unterschied.',
    suggestionTitle: '👉 Was viele aktuell ausprobieren',
    suggestionContent:
      'Viele berichten nach einer Umstellung auf ergonomische Schlafunterstützung von weniger Verspannungen, ruhigerem Schlaf, weniger nächtlichem Aufwachen und mehr Energie am Morgen.',
    ctaButton: {
      text: 'Jetzt mehr erfahren',
      link: '/artikel',
    },
    productIntegration: {
      heading: 'Mehr über diese Lösung erfahren',
      content:
        'Ergonomische Kissen passen sich der natürlichen Haltung von Kopf und Nacken an. Sie können helfen, Verspannungen zu reduzieren und die Schlafqualität spürbar zu verbessern – ohne Medikamente, ohne Gewöhnungseffekt.',
    },
    trustPoints: [
      'Keine Wunderversprechen – nur durchdachte Unterstützung',
      'Basierend auf ergonomischen Erkenntnissen',
      'Kein Medikament, kein Gewöhnungseffekt',
      'Für alle, die mehr aus ihrer Erholung herausholen möchten',
    ],
    midArticleCtaAfterSection: 5,
    images: [
      {
        afterSection: 3,
        src: 'https://cdn.magicpatterns.com/uploads/8iguQUjJsdSRu2xWPC6jLD/4.png',
        alt: 'Frauen mit Schlafmasken in der S-Bahn – Aufschriften: Nur noch 5 Minuten, Ich schlafe noch 20 Minuten',
      },
    ],
    relatedSlugs: [
      'besser-schlafen',
      'muedigkeit-nach-schlaf',
      'hormone-gleichgewicht',
    ],
  },
  {
    slug: 'muedigkeit-nach-schlaf',
    title: 'Warum du dich oft müde fühlst – selbst nach dem Schlaf',
    category: 'Energie',
    preview:
      'Müdigkeit hat selten nur eine Ursache. Wir schauen uns an, was wirklich dahinterstecken kann.',
    readTime: '5 Min. Lesezeit',
    iconName: 'ZapIcon',
    bgGradient: 'from-vita-orange-pale to-white',
    categoryBg: 'bg-vita-orange-pale',
    categoryText: 'text-vita-orange',
    intro:
      'Du schläfst eigentlich genug – und trotzdem fühlst du dich morgens wie gerädert? Damit bist du nicht allein. Viele Frauen kennen dieses Gefühl, und die Ursachen sind oft vielfältiger, als man denkt.',
    keyTakeaways: [
      'Müdigkeit kann viele Ursachen haben – von Nährstoffmangel bis Stress',
      'Eisen, Vitamin D und B12 spielen eine zentrale Rolle',
      'Kleine Veränderungen im Alltag können viel bewirken',
    ],
    sections: [
      {
        heading: 'Mehr als nur „schlecht geschlafen"',
        content:
          'Chronische Müdigkeit ist kein Zeichen von Schwäche – sie ist ein Signal deines Körpers. Häufig stecken Nährstoffmängel dahinter, die sich schleichend entwickeln. Besonders Eisen, Vitamin D und Vitamin B12 sind bei Frauen oft nicht ausreichend vorhanden. Auch eine Schilddrüsenunterfunktion oder hormonelle Schwankungen können dafür verantwortlich sein. Es lohnt sich, genauer hinzuschauen, statt einfach mehr Kaffee zu trinken.',
      },
      {
        heading: 'Die Rolle von Stress und Nervensystem',
        content:
          'Dauerstress versetzt dein Nervensystem in einen permanenten Alarmzustand. Selbst wenn du im Bett liegst, kommt dein Körper nicht wirklich zur Ruhe. Das Stresshormon Cortisol bleibt erhöht, der Schlaf wird oberflächlicher, und die Erholung bleibt aus. Viele Frauen unterschätzen, wie sehr emotionaler Stress – auch der „leise" Alltagsstress – den Körper erschöpft.',
      },
      {
        heading: 'Was du selbst beobachten kannst',
        content:
          'Achte darauf, wann die Müdigkeit besonders stark ist: Ist es morgens direkt nach dem Aufwachen? Nach dem Essen? Am Nachmittag? Diese Muster können Hinweise geben. Ein Ernährungstagebuch oder ein kurzer Check-up beim Hausarzt mit Blutbild kann oft schon Klarheit bringen. Auch dein Zyklus kann eine Rolle spielen – viele Frauen fühlen sich in bestimmten Phasen deutlich erschöpfter.',
      },
    ],
    meaningForYou:
      'Wenn du morgens aufwachst und dich fühlst, als hättest du gar nicht geschlafen, versucht dein Körper dir etwas mitzuteilen. Oft greifen wir dann zum dritten Kaffee, aber das überdeckt nur das Symptom. Es lohnt sich, genauer hinzuschauen: Fehlt dir vielleicht ein bestimmter Nährstoff? Oder ist dein Nervensystem durch Dauerstress überlastet? Du musst nicht alles auf einmal ändern – aber ein erster Schritt kann schon viel bewirken.',
    suggestionTitle: 'Was viele in dieser Situation ausprobieren',
    suggestionContent:
      'Ein sanfter Start in den Tag ohne sofortigen Blick aufs Handy, ein großes Glas warmes Wasser vor dem ersten Kaffee und ein kurzer Check-up der wichtigsten Blutwerte beim Hausarzt können erste, wertvolle Schritte sein, um die eigene Energie zurückzugewinnen.',
    relatedSlugs: [
      'frauen-schlafmangel',
      'besser-schlafen',
      'hormone-gleichgewicht',
    ],
  },
  {
    slug: 'blaehbauch-ursachen',
    title: 'Blähbauch, Stress oder Hormone? Die echten Ursachen',
    category: 'Verdauung',
    preview:
      'Ein aufgeblähter Bauch ist mehr als nur unangenehm. Hier erfährst du, was dein Körper dir damit sagen will.',
    readTime: '7 Min. Lesezeit',
    iconName: 'LeafIcon',
    bgGradient: 'from-vita-green-pale to-white',
    categoryBg: 'bg-vita-green-light',
    categoryText: 'text-vita-green-deep',
    intro:
      'Dein Bauch fühlt sich aufgebläht an, obwohl du gar nicht viel gegessen hast? Das kennen viele Frauen – und die Ursache liegt oft nicht nur im Essen. Stress, Hormone und sogar deine Darmflora können eine Rolle spielen.',
    keyTakeaways: [
      'Ein Blähbauch hat oft mehrere Ursachen gleichzeitig',
      'Stress beeinflusst die Verdauung stärker als viele denken',
      'Hormonelle Schwankungen im Zyklus können Blähungen verstärken',
    ],
    sections: [
      {
        heading: 'Dein Darm und dein Nervensystem',
        content:
          'Dein Darm hat ein eigenes Nervensystem – das sogenannte „Bauchhirn". Es reagiert empfindlich auf Stress, Angst und emotionale Belastung. Wenn du unter Druck stehst, verlangsamt sich die Verdauung, Gase stauen sich, und der Bauch bläht sich auf. Das ist keine Einbildung, sondern eine ganz reale körperliche Reaktion. Deshalb hilft es oft mehr, Stress zu reduzieren, als bestimmte Lebensmittel wegzulassen.',
      },
      {
        heading: 'Hormone und Verdauung',
        content:
          'Progesteron, das nach dem Eisprung ansteigt, hat eine entspannende Wirkung auf die glatte Muskulatur – auch im Darm. Das kann die Verdauung verlangsamen und zu Blähungen führen. Viele Frauen bemerken, dass ihr Bauch in der zweiten Zyklushälfte deutlich empfindlicher reagiert. Auch in den Wechseljahren verändern sich die Hormonspiegel, was die Verdauung beeinflussen kann.',
      },
      {
        heading: 'Nahrungsmittel, die oft unterschätzt werden',
        content:
          'Nicht immer sind es die offensichtlichen Übeltäter wie Bohnen oder Kohl. Auch Brot, Milchprodukte oder zu viel Obst auf einmal können bei empfindlichen Menschen Blähungen auslösen. Fruktose und Laktose werden von vielen Menschen nicht optimal verdaut. Ein Ernährungstagebuch über zwei bis drei Wochen kann helfen, individuelle Auslöser zu erkennen.',
      },
    ],
    meaningForYou:
      'Wenn dein Bauch regelmäßig aufgebläht ist, ist das kein Zeichen dafür, dass du etwas falsch machst. Dein Körper reagiert auf eine Kombination aus Ernährung, Stress und Hormonen. Statt dich zu stressen, lohnt es sich, neugierig hinzuschauen: Was hast du gegessen? Wie war dein Tag? Wo stehst du im Zyklus? Mit der Zeit erkennst du Muster – und kannst gezielter gegensteuern.',
    suggestionTitle: 'Was viele in dieser Situation ausprobieren',
    suggestionContent:
      'Langsamer essen, gut kauen und nach dem Essen einen kurzen Spaziergang machen – das klingt einfach, kann aber erstaunlich viel bewirken. Auch probiotische Lebensmittel wie Joghurt oder Sauerkraut können die Darmflora unterstützen.',
    relatedSlugs: [
      'hormone-gleichgewicht',
      'muedigkeit-nach-schlaf',
      'haut-spiegel-gesundheit',
    ],
  },
  {
    slug: 'vitamin-c-wirkung',
    title: 'Vitamin C: Wann es wirklich etwas bringt',
    category: 'Vitamine',
    preview:
      'Nicht jedes Vitamin-C-Produkt ist gleich. Wir erklären, worauf es wirklich ankommt.',
    readTime: '4 Min. Lesezeit',
    iconName: 'SparklesIcon',
    bgGradient: 'from-vita-peach to-white',
    categoryBg: 'bg-vita-peach',
    categoryText: 'text-vita-orange',
    intro:
      'Vitamin C kennt jeder – aber wann bringt es wirklich etwas? Und wie viel braucht dein Körper tatsächlich? Wir räumen mit Mythen auf und erklären, was die Forschung sagt.',
    keyTakeaways: [
      'Vitamin C ist wichtig für Immunsystem, Haut und Eisenaufnahme',
      'Die meisten Menschen bekommen genug über die Ernährung',
      'Hochdosierte Präparate sind selten nötig – und nicht immer sinnvoll',
    ],
    sections: [
      {
        heading: 'Was Vitamin C im Körper macht',
        content:
          'Vitamin C ist an über 150 Stoffwechselprozessen beteiligt. Es unterstützt das Immunsystem, schützt Zellen vor oxidativem Stress und ist essentiell für die Kollagenbildung – also wichtig für Haut, Bindegewebe und Wundheilung. Außerdem verbessert es die Aufnahme von Eisen aus pflanzlichen Quellen. Ein echter Allrounder, der oft unterschätzt wird.',
      },
      {
        heading: 'Brauche ich ein Supplement?',
        content:
          'Die ehrliche Antwort: Meistens nicht. Wer regelmäßig frisches Obst und Gemüse isst, deckt seinen Bedarf in der Regel gut ab. Eine Paprika enthält zum Beispiel mehr Vitamin C als eine Orange. Supplements können sinnvoll sein bei einseitiger Ernährung, in Stressphasen oder bei bestimmten Erkrankungen. Aber die Dosis macht den Unterschied – mehr ist nicht automatisch besser.',
      },
      {
        heading: 'Worauf du bei Produkten achten solltest',
        content:
          'Wenn du dich für ein Vitamin-C-Präparat entscheidest, achte auf die Form: Ascorbinsäure ist die gängigste, aber für empfindliche Mägen kann gepuffertes Vitamin C (z.B. Calciumascorbat) verträglicher sein. Natürliche Quellen wie Acerola oder Hagebutte enthalten zusätzlich sekundäre Pflanzenstoffe. Wichtig ist auch: Vitamin C ist wasserlöslich – dein Körper speichert keinen Überschuss.',
      },
    ],
    meaningForYou:
      'Du musst nicht jeden Tag ein Supplement nehmen, um gut versorgt zu sein. Wenn du auf eine abwechslungsreiche Ernährung mit viel frischem Gemüse und Obst achtest, bist du wahrscheinlich gut aufgestellt. In stressigen Phasen oder im Winter kann eine gezielte Ergänzung sinnvoll sein – aber lass dich nicht von Marketing-Versprechen verunsichern.',
    suggestionTitle: 'Was viele in dieser Situation ausprobieren',
    suggestionContent:
      'Morgens ein Glas warmes Wasser mit frischem Zitronensaft, regelmäßig Paprika, Brokkoli und Beeren in den Speiseplan einbauen – und in stressigen Zeiten ein niedrig dosiertes, natürliches Vitamin-C-Präparat als Unterstützung.',
    relatedSlugs: [
      'muedigkeit-nach-schlaf',
      'haut-spiegel-gesundheit',
      'blaehbauch-ursachen',
    ],
  },
  {
    slug: 'haut-spiegel-gesundheit',
    title: 'Warum deine Haut dir zeigt, was innen passiert',
    category: 'Haut & Haare',
    preview:
      'Unreine Haut, Trockenheit oder Rötungen? Deine Haut spiegelt oft wider, was im Körper los ist.',
    readTime: '6 Min. Lesezeit',
    iconName: 'SparklesIcon',
    bgGradient: 'from-vita-peach to-vita-orange-pale',
    categoryBg: 'bg-vita-peach',
    categoryText: 'text-vita-orange',
    intro:
      'Deine Haut ist dein größtes Organ – und gleichzeitig ein Spiegel deiner inneren Gesundheit. Wenn sie plötzlich unrein wird, extrem trocken ist oder sich rötet, lohnt es sich, nicht nur nach außen zu schauen.',
    keyTakeaways: [
      'Hautveränderungen können auf hormonelle Schwankungen hinweisen',
      'Darmgesundheit und Hautbild hängen eng zusammen',
      'Stress zeigt sich oft zuerst im Gesicht',
    ],
    sections: [
      {
        heading: 'Die Darm-Haut-Achse',
        content:
          'Forschung zeigt immer deutlicher: Darm und Haut kommunizieren miteinander. Eine gestörte Darmflora kann Entzündungen im Körper fördern, die sich als Akne, Rosacea oder Ekzeme auf der Haut zeigen. Wenn du merkst, dass deine Haut nach bestimmten Mahlzeiten reagiert, könnte das ein Hinweis auf eine Nahrungsmittelunverträglichkeit oder ein Ungleichgewicht im Darm sein.',
      },
      {
        heading: 'Hormone und Hautbild',
        content:
          'Viele Frauen kennen es: Vor der Periode verschlechtert sich das Hautbild. Das liegt am sinkenden Östrogenspiegel und dem relativen Anstieg von Androgenen. Auch in den Wechseljahren verändert sich die Haut – sie wird oft trockener und dünner. Hormonelle Verhütungsmittel können das Hautbild ebenfalls beeinflussen, positiv wie negativ.',
      },
      {
        heading: 'Was deine Haut wirklich braucht',
        content:
          'Gute Hautpflege beginnt von innen: ausreichend Wasser trinken, Omega-3-Fettsäuren über Lebensmittel wie Lachs, Walnüsse oder Leinsamen aufnehmen und auf genügend Zink und Vitamin A achten. Von außen hilft eine sanfte, nicht zu aggressive Pflegeroutine. Weniger ist oft mehr – zu viele Produkte können die Hautbarriere stören.',
      },
    ],
    meaningForYou:
      'Wenn deine Haut verrücktspielt, ist das frustrierend – aber auch eine Einladung, genauer hinzuhören. Statt immer neue Cremes auszuprobieren, kann es helfen, einen Blick auf deine Ernährung, deinen Stress und deine Hormone zu werfen. Deine Haut erzählt eine Geschichte – und du kannst lernen, sie zu lesen.',
    suggestionTitle: 'Was viele in dieser Situation ausprobieren',
    suggestionContent:
      'Eine einfache Pflegeroutine mit wenigen, hochwertigen Produkten, mehr Omega-3-reiche Lebensmittel im Alltag und ein bewusster Blick auf den Zusammenhang zwischen Zyklus und Hautbild.',
    relatedSlugs: [
      'hormone-gleichgewicht',
      'blaehbauch-ursachen',
      'vitamin-c-wirkung',
    ],
  },
  {
    slug: 'besser-schlafen',
    title: 'Besser schlafen: Was dein Körper nachts wirklich braucht',
    category: 'Schlaf',
    preview:
      'Guter Schlaf ist mehr als früh ins Bett gehen. Erfahre, was deinen Schlaf wirklich beeinflusst.',
    readTime: '6 Min. Lesezeit',
    iconName: 'MoonIcon',
    bgGradient: 'from-[#F0EBF5] to-white',
    categoryBg: 'bg-[#F0EBF5]',
    categoryText: 'text-[#8B7BAF]',
    intro:
      'Du liegst im Bett, bist müde – aber der Schlaf will einfach nicht kommen? Oder du wachst nachts auf und grübelst? Schlafprobleme sind weit verbreitet, besonders bei Frauen. Die gute Nachricht: Du kannst mehr beeinflussen, als du denkst.',
    keyTakeaways: [
      'Schlafqualität ist wichtiger als Schlafdauer',
      'Licht, Temperatur und Routinen beeinflussen den Schlaf enorm',
      'Hormonelle Veränderungen können Schlafprobleme verstärken',
    ],
    sections: [
      {
        heading: 'Warum Schlaf so wichtig ist',
        content:
          'Im Schlaf passiert mehr, als du denkst: Dein Körper repariert Zellen, dein Gehirn verarbeitet Erlebnisse und sortiert Erinnerungen, dein Immunsystem wird gestärkt. Chronischer Schlafmangel erhöht das Risiko für Herz-Kreislauf-Erkrankungen, Übergewicht und Depressionen. Guter Schlaf ist keine Luxus – er ist die Grundlage für alles andere.',
      },
      {
        heading: 'Was deinen Schlaf stört',
        content:
          'Blaues Licht von Bildschirmen unterdrückt die Melatoninproduktion und signalisiert deinem Gehirn: Es ist noch Tag. Koffein hat eine Halbwertszeit von etwa 5 Stunden – ein Kaffee um 15 Uhr wirkt also noch um 20 Uhr. Auch Alkohol, obwohl er müde macht, verschlechtert die Schlafqualität deutlich. Und ein zu warmes Schlafzimmer verhindert, dass dein Körper die nötige Kerntemperatur-Absenkung erreicht.',
      },
      {
        heading: 'Schlaf und weibliche Hormone',
        content:
          'Östrogen und Progesteron beeinflussen den Schlaf direkt. In der zweiten Zyklushälfte, wenn Progesteron steigt, schlafen manche Frauen besser – andere schlechter. In den Wechseljahren können Hitzewallungen und sinkende Hormonspiegel den Schlaf massiv stören. Auch Schwangerschaft und Stillzeit verändern den Schlafrhythmus grundlegend. Es ist wichtig zu wissen: Das ist normal und kein persönliches Versagen.',
      },
    ],
    meaningForYou:
      'Wenn du schlecht schläfst, bist du nicht allein – und es liegt nicht daran, dass du etwas falsch machst. Dein Körper reagiert auf viele Faktoren gleichzeitig: Licht, Stress, Hormone, Ernährung. Statt dich unter Druck zu setzen, kannst du Schritt für Schritt herausfinden, was dir persönlich hilft. Manchmal sind es die kleinen Dinge, die den größten Unterschied machen.',
    suggestionTitle: 'Was viele in dieser Situation ausprobieren',
    suggestionContent:
      'Eine feste Abendroutine ohne Bildschirm ab 21 Uhr, ein kühles Schlafzimmer (16–18°C), Magnesium am Abend und ein kurzes Dankbarkeits- oder Atemübung vor dem Einschlafen.',
    relatedSlugs: [
      'frauen-schlafmangel',
      'muedigkeit-nach-schlaf',
      'hormone-gleichgewicht',
    ],
  },
  {
    slug: 'hormone-gleichgewicht',
    title: 'Hormone im Gleichgewicht: Was Frauen wissen sollten',
    category: 'Hormone',
    preview:
      'Hormone steuern mehr, als wir denken. Verstehe, wie sie dein Wohlbefinden beeinflussen.',
    readTime: '8 Min. Lesezeit',
    iconName: 'HeartPulseIcon',
    bgGradient: 'from-vita-green-light to-vita-green-pale',
    categoryBg: 'bg-vita-green-light',
    categoryText: 'text-vita-green-deep',
    intro:
      'Hormone sind die unsichtbaren Dirigenten deines Körpers. Sie beeinflussen deine Stimmung, deine Energie, deinen Schlaf, dein Gewicht und sogar deine Haut. Wenn sie aus dem Gleichgewicht geraten, spürst du das oft an vielen Stellen gleichzeitig.',
    keyTakeaways: [
      'Hormone beeinflussen Stimmung, Energie, Schlaf und Haut gleichzeitig',
      'Der weibliche Zyklus ist ein wichtiger Gesundheitsindikator',
      'Lebensstil-Faktoren wie Ernährung und Stress haben großen Einfluss',
    ],
    sections: [
      {
        heading: 'Die wichtigsten Hormone im Überblick',
        content:
          'Östrogen, Progesteron, Testosteron, Cortisol, Insulin, Schilddrüsenhormone – sie alle arbeiten zusammen wie ein Orchester. Wenn eines aus dem Takt gerät, beeinflusst das die anderen. Östrogen sorgt unter anderem für gute Stimmung und geschmeidige Haut, Progesteron für Ruhe und guten Schlaf. Cortisol ist das Stresshormon, das in kleinen Mengen wichtig ist, aber in Dauerstress zum Problem wird.',
      },
      {
        heading: 'Anzeichen für ein Ungleichgewicht',
        content:
          'Unregelmäßige Zyklen, starke PMS-Symptome, Haarausfall, unerklärliche Gewichtszunahme, Schlafstörungen, Stimmungsschwankungen oder anhaltende Müdigkeit können Hinweise auf ein hormonelles Ungleichgewicht sein. Oft werden diese Symptome einzeln behandelt, obwohl sie zusammenhängen. Ein ganzheitlicher Blick – idealerweise mit einem Hormonspeicheltest oder Blutbild – kann Klarheit bringen.',
      },
      {
        heading: 'Was du selbst tun kannst',
        content:
          'Dein Lebensstil hat einen enormen Einfluss auf deine Hormone. Regelmäßige Mahlzeiten mit ausreichend Protein und gesunden Fetten stabilisieren den Blutzucker und damit auch Insulin und Cortisol. Bewegung – besonders moderate Ausdauer und Krafttraining – unterstützt die Hormonbalance. Und ausreichend Schlaf ist die Basis für die nächtliche Hormonproduktion. Auch Stressmanagement durch Meditation, Yoga oder einfach bewusste Pausen kann viel bewirken.',
      },
    ],
    meaningForYou:
      'Deine Hormone sind kein Feind – sie sind ein fein abgestimmtes System, das auf dein Leben reagiert. Wenn du dich unwohl fühlst, ist das oft ein Zeichen, dass dieses System Unterstützung braucht. Du musst kein Experte sein, um deinen Körper besser zu verstehen. Fang damit an, deinen Zyklus zu beobachten, auf deine Ernährung zu achten und dir bewusst Pausen zu gönnen.',
    suggestionTitle: 'Was viele in dieser Situation ausprobieren',
    suggestionContent:
      'Zyklustracking mit einer App, um Muster zu erkennen. Regelmäßige Mahlzeiten mit Protein und gesunden Fetten. Adaptogene Kräuter wie Ashwagandha oder Mönchspfeffer nach Absprache mit dem Arzt. Und vor allem: Geduld mit sich selbst.',
    relatedSlugs: [
      'besser-schlafen',
      'haut-spiegel-gesundheit',
      'muedigkeit-nach-schlaf',
    ],
  },
]

export function getArticleBySlug(slug: string): Article | undefined {
  return articles.find((a) => a.slug === slug)
}

export function getRelatedArticles(
  currentSlug: string,
  relatedSlugs: string[],
): Article[] {
  return relatedSlugs
    .map((s) => articles.find((a) => a.slug === s))
    .filter((a): a is Article => a !== undefined && a.slug !== currentSlug)
}
